import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// Form builder for /juniors/:jid/jestionform/forms/:id/edit.
// Owns the canvas state and persists every change to the existing
// /api/v1/jestionform/* endpoints. No optimistic UI: each change waits for
// the API to confirm, then updates the DOM. Status pill at the top of the
// page reflects {saving, saved, error}.
//
// Targets:
//   titleHeader, statusBadge, saveStatus
//   titleInput, descInput, anonInput, publicInput
//   sectionsList, sectionCard
//   fieldsList, fieldCard
//   fieldPickerSectionId
//   empty
//
// Values:
//   formId, formUpdateUrl, statusUpdateUrl,
//   sectionCreateUrl, sectionUpdateUrlTemplate, sectionPositionsUrl,
//   fieldCreateUrl,   fieldUpdateUrlTemplate,   fieldPositionsUrl,
//   csrfToken
//
// Markup contract: section-card / field-card partials carry data-section-id /
// data-field-id and the appropriate `data-jestionform-form-builder-target`.
export default class extends Controller {
  static targets = [
    "titleHeader", "statusBadge", "saveStatus",
    "titleInput", "descInput", "anonInput", "publicInput",
    "sectionsList", "sectionCard",
    "fieldsList", "fieldCard",
    "fieldPickerSectionId",
    "empty",
    // Field edit modal
    "fieldEditPanel", "fieldEditId", "fieldEditType", "fieldEditTypeLabel",
    "fieldEditLabel", "fieldEditHelp", "fieldEditPlaceholder", "fieldEditPlaceholderRow",
    "fieldEditRequired",
    "fieldEditChoicesRow", "fieldEditChoicesList",
    "fieldEditError",
    "choiceRowTemplate", "choiceInput",
    // Theme customizer (Apparence)
    "themePrimary", "themeAccent", "themeText", "themeBg",
    "themeLogo", "themePreview", "themePreviewLogo"
  ];

  static values = {
    formId:                    String,
    formUpdateUrl:             String,
    statusUpdateUrl:           String,
    sectionCreateUrl:          String,
    sectionUpdateUrlTemplate:  String,
    sectionPositionsUrl:       String,
    fieldCreateUrl:            String,
    fieldUpdateUrlTemplate:    String,
    fieldPositionsUrl:         String,
    csrfToken:                 String
  };

  // ── Lifecycle ───────────────────────────────────────────────────────

  connect() {
    this._initSortables();
    this._titleSaveTimer = null;
  }

  disconnect() {
    this._destroySortables();
    if (this._choicesSortable) {
      try { this._choicesSortable.destroy(); } catch (_e) {}
      this._choicesSortable = null;
    }
    if (this._titleSaveTimer) clearTimeout(this._titleSaveTimer);
  }

  _initSortables() {
    this._destroySortables();
    this._sortables = [];

    if (this.hasSectionsListTarget) {
      this._sortables.push(Sortable.create(this.sectionsListTarget, {
        animation: 150,
        handle: ".jf-section-card__handle",
        ghostClass: "sortable-ghost",
        dragClass: "sortable-drag",
        draggable: ".jf-section-card",
        onEnd: () => this._persistSectionPositions()
      }));
    }

    this.fieldsListTargets.forEach((list) => {
      this._sortables.push(Sortable.create(list, {
        group: "jf-fields",
        animation: 150,
        handle: ".jf-field-card__handle",
        ghostClass: "sortable-ghost",
        dragClass: "sortable-drag",
        draggable: ".jf-field-card",
        onAdd:  (evt) => this._onFieldMoved(evt),
        onEnd:  (evt) => { if (evt.from === evt.to) this._persistFieldPositions(); }
      }));
    });
  }

  _destroySortables() {
    (this._sortables || []).forEach((s) => { try { s.destroy(); } catch (_) {} });
    this._sortables = [];
  }

  // ── Save state indicator ────────────────────────────────────────────

  _setState(state, message) {
    if (!this.hasSaveStatusTarget) return;
    const el = this.saveStatusTarget;
    const messages = {
      saving: '<i class="fa-solid fa-spinner fa-spin"></i> Enregistrement…',
      saved:  '<i class="fa-solid fa-circle-check"></i> Toutes les modifications sont enregistrées',
      error:  `<i class="fa-solid fa-circle-exclamation"></i> ${message || "Une erreur est survenue."}`
    };
    el.innerHTML = messages[state] || messages.saved;
    el.dataset.state = state;
  }

  // ── Metadata (title, description, anonymous, public_access) ─────────

  scheduleTitleSave(event) {
    // Reflect title in the header live
    if (this.hasTitleHeaderTarget) {
      const v = (event.target.value || "").trim();
      this.titleHeaderTarget.textContent = v.length ? v : "Formulaire sans titre";
    }
    if (this._titleSaveTimer) clearTimeout(this._titleSaveTimer);
    this._titleSaveTimer = setTimeout(() => this.saveMetadata(event), 800);
  }

  async saveMetadata(event) {
    if (this._titleSaveTimer) { clearTimeout(this._titleSaveTimer); this._titleSaveTimer = null; }
    const el = event.target;
    const attr = el.dataset.attr;
    if (!attr) return;

    let value;
    if (el.type === "checkbox") {
      value = el.checked;
    } else {
      value = (el.value || "").trim();
    }

    if (attr === "title" && !value) {
      // Title can't be empty — restore previous value silently.
      if (el.dataset.lastValue) el.value = el.dataset.lastValue;
      this._setState("error", "Le titre ne peut pas être vide.");
      return;
    }
    el.dataset.lastValue = el.value;

    this._setState("saving");
    try {
      const res = await fetch(this.formUpdateUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ form: { [attr]: value } })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.errors || [err.error || "Échec de l'enregistrement"]).join(", "));
      }
      this._setState("saved");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  async changeStatus(event) {
    const status = event.currentTarget.dataset.status;
    if (!status) return;

    this._setState("saving");
    try {
      const res = await fetch(this.statusUpdateUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ status })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Échec du changement de statut");
      }
      const data = await res.json();
      this._setState("saved");
      // Refresh the status pill in the header
      if (this.hasStatusBadgeTarget) {
        const label = data.status === "published" ? "Publié" : (data.status === "archived" ? "Archivé" : "Brouillon");
        this.statusBadgeTarget.className = `jf-status jf-status--${data.status}`;
        this.statusBadgeTarget.textContent = label;
      }
      // Public access may have been disabled server-side
      if (this.hasPublicInputTarget && typeof data.public_access_enabled === "boolean") {
        this.publicInputTarget.checked = data.public_access_enabled;
      }
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  // ── Sections ────────────────────────────────────────────────────────

  async addSection() {
    this._setState("saving");
    try {
      const res = await fetch(this.sectionCreateUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ section: { title: "Nouvelle section" } })
      });
      if (!res.ok) throw new Error("Création de la section impossible.");
      const section = await res.json();
      this._appendSectionCard(section);
      this._setState("saved");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  async saveSectionTitle(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    const title = event.currentTarget.value.trim();
    if (!sectionId) return;
    this._setState("saving");
    try {
      const res = await fetch(this.sectionUpdateUrlTemplateValue.replace("__id__", sectionId), {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ section: { title } })
      });
      if (!res.ok) throw new Error("Impossible de sauvegarder le titre de la section.");
      this._setState("saved");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  async deleteSection(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    if (!sectionId) return;
    if (!confirm("Supprimer cette section et tous ses champs ?")) return;

    this._setState("saving");
    try {
      const res = await fetch(this.sectionUpdateUrlTemplateValue.replace("__id__", sectionId), {
        method: "DELETE",
        headers: this._jsonHeaders(),
        credentials: "same-origin"
      });
      if (!res.ok && res.status !== 204) throw new Error("Suppression impossible.");
      const card = this.sectionCardTargets.find((c) => c.dataset.sectionId === sectionId);
      card?.remove();
      this._setState("saved");
      this._refreshEmptyState();
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  async _persistSectionPositions() {
    const order = this.sectionCardTargets.map((card, idx) => ({
      id: card.dataset.sectionId, position: idx + 1
    }));
    this._setState("saving");
    try {
      const res = await fetch(this.sectionPositionsUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ order })
      });
      if (!res.ok && res.status !== 204) throw new Error("Réorganisation impossible.");
      this._setState("saved");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  // ── Fields ──────────────────────────────────────────────────────────

  openFieldPicker(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    if (this.hasFieldPickerSectionIdTarget) {
      this.fieldPickerSectionIdTarget.value = sectionId || "";
    }
    this._openModal("jestionform_field_picker");
  }

  async pickFieldType(event) {
    const fieldType = event.currentTarget.dataset.fieldType;
    const fieldLabel = event.currentTarget.dataset.fieldLabel || "Nouveau champ";
    const sectionId = this.hasFieldPickerSectionIdTarget ? this.fieldPickerSectionIdTarget.value : "";
    if (!fieldType || !sectionId) return;

    this._closeModal("jestionform_field_picker");

    // Compute next position within this section
    const sectionCard = this.sectionCardTargets.find((c) => c.dataset.sectionId === sectionId);
    const fieldsList = sectionCard?.querySelector('[data-jestionform-form-builder-target="fieldsList"]');
    const nextPos = fieldsList ? fieldsList.querySelectorAll(".jf-field-card").length + 1 : 1;

    this._setState("saving");
    try {
      const res = await fetch(this.fieldCreateUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          field: {
            label: fieldLabel,
            field_type: fieldType,
            position: nextPos,
            jestionform_section_id: sectionId
          }
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.errors || ["Création du champ impossible"]).join(", "));
      }
      const field = await res.json();
      this._appendFieldCard(sectionId, field);
      this._setState("saved");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  async deleteField(event) {
    const fieldId = event.currentTarget.dataset.fieldId;
    if (!fieldId) return;
    if (!confirm("Supprimer ce champ ?")) return;

    this._setState("saving");
    try {
      const res = await fetch(this.fieldUpdateUrlTemplateValue.replace("__id__", fieldId), {
        method: "DELETE",
        headers: this._jsonHeaders(),
        credentials: "same-origin"
      });
      if (!res.ok && res.status !== 204) throw new Error("Suppression impossible.");
      const card = this.fieldCardTargets.find((c) => c.dataset.fieldId === fieldId);
      const parentList = card?.closest('[data-jestionform-form-builder-target="fieldsList"]');
      card?.remove();
      this._setState("saved");
      // Refresh "empty" message inside the section
      if (parentList && !parentList.querySelector(".jf-field-card") && !parentList.querySelector(".jf-section-card__empty")) {
        const empty = document.createElement("p");
        empty.className = "jf-section-card__empty";
        empty.textContent = "Aucun champ dans cette section.";
        parentList.appendChild(empty);
      }
      // Update count badge
      this._refreshSectionCount(parentList?.dataset.sectionId);
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  async toggleRequired(event) {
    const fieldId = event.currentTarget.dataset.fieldId;
    if (!fieldId) return;
    const btn = event.currentTarget;
    const card = this.fieldCardTargets.find((c) => c.dataset.fieldId === fieldId);
    const wasActive = btn.classList.contains("is-active");
    const next = !wasActive;

    this._setState("saving");
    try {
      const res = await fetch(this.fieldUpdateUrlTemplateValue.replace("__id__", fieldId), {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ field: { required: next } })
      });
      if (!res.ok) throw new Error("Mise à jour impossible.");
      btn.classList.toggle("is-active", next);
      btn.title = next ? "Retirer obligatoire" : "Rendre obligatoire";

      // Keep the embedded payload in sync so editField() sees the new state
      if (card?.dataset.fieldPayload) {
        try {
          const payload = JSON.parse(card.dataset.fieldPayload);
          payload.required = next;
          card.dataset.fieldPayload = JSON.stringify(payload);
        } catch (_e) { /* noop */ }
      }

      // Update label asterisk
      const labelEl = card?.querySelector(".jf-field-card__label");
      if (labelEl) {
        let star = labelEl.querySelector(".jf-field-card__required");
        if (next && !star) {
          star = document.createElement("span");
          star.className = "jf-field-card__required";
          star.title = "Obligatoire";
          star.textContent = "*";
          labelEl.appendChild(document.createTextNode(" "));
          labelEl.appendChild(star);
        } else if (!next && star) {
          star.remove();
        }
      }
      this._setState("saved");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  // ── Theme customizer (Apparence) ────────────────────────────────────

  applyThemePreset(event) {
    event?.preventDefault?.();
    const t = event.currentTarget.dataset;
    if (this.hasThemePrimaryTarget) this.themePrimaryTarget.value = t.themePrimary || "#2563eb";
    if (this.hasThemeAccentTarget)  this.themeAccentTarget.value  = t.themeAccent  || "#f59e0b";
    if (this.hasThemeTextTarget)    this.themeTextTarget.value    = t.themeText    || "#0f172a";
    if (this.hasThemeBgTarget)      this.themeBgTarget.value      = t.themeBg      || "#ffffff";
    this._refreshThemePreview();
    this.saveTheme();
  }

  async saveTheme() {
    this._refreshThemePreview();
    const payload = {
      colors: {
        primary: this.themePrimaryTarget?.value || "#2563eb",
        accent:  this.themeAccentTarget?.value  || "#f59e0b",
        text:    this.themeTextTarget?.value    || "#0f172a"
      },
      background: {
        color: this.themeBgTarget?.value || "#ffffff"
      },
      header: {
        imageUrl: (this.themeLogoTarget?.value || "").trim()
      }
    };

    this._setState("saving");
    try {
      const res = await fetch(this.formUpdateUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ form: { theme_settings: payload } })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.errors || ["Mise à jour du thème impossible"]).join(", "));
      }
      this._setState("saved");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  _refreshThemePreview() {
    if (!this.hasThemePreviewTarget) return;
    const preview = this.themePreviewTarget;
    if (this.hasThemePrimaryTarget) preview.style.setProperty("--jf-tp-primary", this.themePrimaryTarget.value);
    if (this.hasThemeAccentTarget)  preview.style.setProperty("--jf-tp-accent",  this.themeAccentTarget.value);
    if (this.hasThemeTextTarget)    preview.style.setProperty("--jf-tp-text",    this.themeTextTarget.value);
    if (this.hasThemeBgTarget)      preview.style.setProperty("--jf-tp-bg",      this.themeBgTarget.value);

    if (this.hasThemePreviewLogoTarget && this.hasThemeLogoTarget) {
      const url = (this.themeLogoTarget.value || "").trim();
      if (url.length) {
        this.themePreviewLogoTarget.src = url;
        this.themePreviewLogoTarget.style.display = "";
      } else {
        this.themePreviewLogoTarget.removeAttribute("src");
        this.themePreviewLogoTarget.style.display = "none";
      }
    }
  }

  // ── Field edit modal ───────────────────────────────────────────────

  // Types qui nécessitent un éditeur d'options.
  static OPTION_TYPES = ["select", "multiselect", "radio", "checkbox"];
  // Types pour lesquels un placeholder fait sens.
  static PLACEHOLDER_TYPES = ["text", "textarea", "email", "phone", "number"];

  editField(event) {
    const fieldId = event.currentTarget.dataset.fieldId;
    if (!fieldId) return;
    const card = this.fieldCardTargets.find((c) => c.dataset.fieldId === fieldId);
    if (!card) return;

    // Decode the payload embedded on the field card
    let payload = {};
    try {
      payload = JSON.parse(card.dataset.fieldPayload || "{}");
    } catch (_e) { payload = {}; }

    const type = payload.field_type || "";
    if (this.hasFieldEditIdTarget)    this.fieldEditIdTarget.value = fieldId;
    if (this.hasFieldEditTypeTarget)  this.fieldEditTypeTarget.value = type;
    if (this.hasFieldEditTypeLabelTarget) {
      const icon = this._typeIcon(type);
      this.fieldEditTypeLabelTarget.innerHTML =
        `<i class="fa-solid ${icon}"></i> ${this._esc(payload.type_label || this._typeLabel(type))}`;
    }
    if (this.hasFieldEditLabelTarget)       this.fieldEditLabelTarget.value       = payload.label || "";
    if (this.hasFieldEditHelpTarget)        this.fieldEditHelpTarget.value        = payload.help_text || "";
    if (this.hasFieldEditPlaceholderTarget) this.fieldEditPlaceholderTarget.value = payload.placeholder || "";
    if (this.hasFieldEditRequiredTarget)    this.fieldEditRequiredTarget.checked  = !!payload.required;

    // Toggle placeholder row visibility based on type
    if (this.hasFieldEditPlaceholderRowTarget) {
      this.fieldEditPlaceholderRowTarget.hidden =
        !this.constructor.PLACEHOLDER_TYPES.includes(type);
    }

    // Toggle choices editor visibility + populate
    const isOptionType = this.constructor.OPTION_TYPES.includes(type);
    if (this.hasFieldEditChoicesRowTarget) {
      this.fieldEditChoicesRowTarget.hidden = !isOptionType;
    }
    if (isOptionType && this.hasFieldEditChoicesListTarget) {
      this.fieldEditChoicesListTarget.innerHTML = "";
      const choices = Array.isArray(payload.choices) ? payload.choices : [];
      if (choices.length === 0) {
        // Pre-populate with two empty rows so the user has somewhere to type
        this._appendChoiceRow("");
        this._appendChoiceRow("");
      } else {
        choices.forEach((c) => this._appendChoiceRow(c));
      }
      this._initChoicesSortable();
    }

    this._hideFieldEditError();
    this._openModal("jestionform_field_edit");

    // Focus the label for quick edit
    setTimeout(() => this.fieldEditLabelTarget?.focus(), 80);
  }

  closeFieldEdit() {
    this._closeModal("jestionform_field_edit");
    if (this._choicesSortable) {
      try { this._choicesSortable.destroy(); } catch (_e) {}
      this._choicesSortable = null;
    }
  }

  addChoice(event) {
    event?.preventDefault?.();
    this._appendChoiceRow("");
    this._initChoicesSortable();
    // Focus the new input
    const inputs = this.fieldEditChoicesListTarget.querySelectorAll('input[type="text"]');
    inputs[inputs.length - 1]?.focus();
  }

  removeChoice(event) {
    event?.preventDefault?.();
    const row = event.currentTarget.closest(".jf-options-editor__row");
    row?.remove();
  }

  async saveFieldEdit() {
    const fieldId = this.fieldEditIdTarget?.value;
    const type    = this.fieldEditTypeTarget?.value;
    if (!fieldId) return;

    const label = (this.fieldEditLabelTarget?.value || "").trim();
    if (!label) {
      this._showFieldEditError("Le libellé est obligatoire.");
      this.fieldEditLabelTarget?.focus();
      return;
    }

    const helpText    = (this.fieldEditHelpTarget?.value || "").trim();
    const placeholder = this.constructor.PLACEHOLDER_TYPES.includes(type)
                        ? (this.fieldEditPlaceholderTarget?.value || "").trim()
                        : "";
    const required    = !!this.fieldEditRequiredTarget?.checked;

    const isOptionType = this.constructor.OPTION_TYPES.includes(type);
    let choices = [];
    if (isOptionType) {
      const inputs = this.fieldEditChoicesListTarget?.querySelectorAll('input[type="text"]') || [];
      choices = Array.from(inputs)
                     .map((i) => i.value.trim())
                     .filter((v) => v.length > 0);
      if (choices.length === 0) {
        this._showFieldEditError("Ajoute au moins une option pour ce type de champ.");
        return;
      }
      // Detect duplicates (case-insensitive)
      const lower = choices.map((c) => c.toLowerCase());
      const hasDup = lower.some((v, i) => lower.indexOf(v) !== i);
      if (hasDup) {
        this._showFieldEditError("Deux options ne peuvent pas avoir le même libellé.");
        return;
      }
    }

    const fieldPayload = {
      label: label,
      help_text: helpText,
      placeholder: placeholder,
      required: required
    };
    if (isOptionType) {
      fieldPayload.options = { choices: choices };
    }

    this._setState("saving");
    try {
      const res = await fetch(this.fieldUpdateUrlTemplateValue.replace("__id__", fieldId), {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ field: fieldPayload })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.errors || ["Mise à jour impossible"]).join(", "));
      }

      // Update the field card UI in-place
      this._updateFieldCardAfterEdit(fieldId, type, { label, helpText, required, choices, placeholder });
      this.closeFieldEdit();
      this._setState("saved");
    } catch (e) {
      this._showFieldEditError(e.message);
      this._setState("error", e.message);
    }
  }

  _appendChoiceRow(value) {
    if (!this.hasChoiceRowTemplateTarget || !this.hasFieldEditChoicesListTarget) return;
    const fragment = this.choiceRowTemplateTarget.content.cloneNode(true);
    const input = fragment.querySelector('input[type="text"]');
    if (input) input.value = value || "";
    this.fieldEditChoicesListTarget.appendChild(fragment);
  }

  _initChoicesSortable() {
    if (!this.hasFieldEditChoicesListTarget) return;
    if (this._choicesSortable) {
      try { this._choicesSortable.destroy(); } catch (_e) {}
    }
    this._choicesSortable = Sortable.create(this.fieldEditChoicesListTarget, {
      animation: 150,
      handle: ".jf-options-editor__handle",
      ghostClass: "sortable-ghost",
      dragClass: "sortable-drag",
      draggable: ".jf-options-editor__row"
    });
  }

  _updateFieldCardAfterEdit(fieldId, type, attrs) {
    const card = this.fieldCardTargets.find((c) => c.dataset.fieldId === fieldId);
    if (!card) return;

    // Update label with required asterisk
    const labelEl = card.querySelector(".jf-field-card__label");
    if (labelEl) {
      labelEl.textContent = "";
      labelEl.appendChild(document.createTextNode(attrs.label));
      if (attrs.required) {
        const star = document.createElement("span");
        star.className = "jf-field-card__required";
        star.title = "Obligatoire";
        star.textContent = "*";
        labelEl.appendChild(document.createTextNode(" "));
        labelEl.appendChild(star);
      }
    }

    // Update type line (with options count for option types)
    const typeEl = card.querySelector(".jf-field-card__type");
    if (typeEl) {
      const typeLabel = this._typeLabel(type);
      let line = typeLabel;
      if (this.constructor.OPTION_TYPES.includes(type)) {
        const n = (attrs.choices || []).length;
        line += ` · ${n} option${n > 1 ? "s" : ""}`;
        if (n === 0) {
          line += " ⚠ aucune option configurée";
        }
      }
      typeEl.textContent = line;
    }

    // Update help text element (add/remove/replace)
    let helpEl = card.querySelector(".jf-field-card__help");
    if (attrs.helpText && attrs.helpText.length) {
      if (!helpEl) {
        helpEl = document.createElement("span");
        helpEl.className = "jf-field-card__help";
        card.querySelector(".jf-field-card__body")?.appendChild(helpEl);
      }
      helpEl.textContent = attrs.helpText.length > 80 ? attrs.helpText.slice(0, 80) + "…" : attrs.helpText;
    } else if (helpEl) {
      helpEl.remove();
    }

    // Update required toggle in actions row
    const reqBtn = card.querySelector(".jf-icon-btn--toggle");
    if (reqBtn) {
      reqBtn.classList.toggle("is-active", !!attrs.required);
      reqBtn.title = attrs.required ? "Retirer obligatoire" : "Rendre obligatoire";
    }

    // Update the embedded payload so subsequent edits see the new state
    let payload = {};
    try { payload = JSON.parse(card.dataset.fieldPayload || "{}"); } catch (_e) {}
    payload.label       = attrs.label;
    payload.help_text   = attrs.helpText;
    payload.required    = !!attrs.required;
    payload.placeholder = attrs.placeholder;
    payload.choices     = attrs.choices || [];
    card.dataset.fieldPayload = JSON.stringify(payload);
  }

  _showFieldEditError(msg) {
    if (!this.hasFieldEditErrorTarget) return;
    this.fieldEditErrorTarget.textContent = msg || "Une erreur est survenue.";
    this.fieldEditErrorTarget.hidden = false;
  }

  _hideFieldEditError() {
    if (!this.hasFieldEditErrorTarget) return;
    this.fieldEditErrorTarget.textContent = "";
    this.fieldEditErrorTarget.hidden = true;
  }

  _openModal(id) {
    const modal = document.getElementById(id);
    if (!modal) return;
    modal.classList.add("esm_modal--open");
    modal.removeAttribute("hidden");
    document.body.classList.add("esm_modal-lock");
  }

  _closeModal(id) {
    const modal = document.getElementById(id);
    if (!modal) return;
    modal.classList.remove("esm_modal--open");
    modal.setAttribute("hidden", "");
    document.body.classList.remove("esm_modal-lock");
  }

  _onFieldMoved(evt) {
    // SortableJS moved a field card to a new section list.
    const card = evt.item;
    const fieldId = card?.dataset.fieldId;
    const newSectionId = evt.to?.dataset.sectionId;
    if (!fieldId || !newSectionId) return;
    card.dataset.sectionId = newSectionId;

    // Update both source and dest count badges
    this._refreshSectionCount(evt.from?.dataset.sectionId);
    this._refreshSectionCount(newSectionId);

    // Persist: PATCH the moved field's section + reorder both lists.
    this._persistFieldSectionMove(fieldId, newSectionId).then(() => this._persistFieldPositions());
  }

  async _persistFieldSectionMove(fieldId, sectionId) {
    try {
      const res = await fetch(this.fieldUpdateUrlTemplateValue.replace("__id__", fieldId), {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ field: { jestionform_section_id: sectionId } })
      });
      if (!res.ok) throw new Error("Déplacement du champ impossible.");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  async _persistFieldPositions() {
    const order = [];
    this.fieldsListTargets.forEach((list) => {
      const cards = list.querySelectorAll(".jf-field-card");
      cards.forEach((card, idx) => {
        order.push({ id: card.dataset.fieldId, position: idx + 1 });
      });
    });
    this._setState("saving");
    try {
      const res = await fetch(this.fieldPositionsUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ order })
      });
      if (!res.ok && res.status !== 204) throw new Error("Réorganisation impossible.");
      this._setState("saved");
    } catch (e) {
      this._setState("error", e.message);
    }
  }

  // ── DOM helpers ─────────────────────────────────────────────────────

  _appendSectionCard(section) {
    if (this.hasEmptyTarget) this.emptyTarget.remove();

    const card = document.createElement("article");
    card.className = "jf-section-card";
    card.dataset.sectionId = section.id;
    card.setAttribute("data-jestionform-form-builder-target", "sectionCard");
    card.innerHTML = `
      <header class="jf-section-card__head">
        <span class="jf-section-card__handle" title="Glisser pour réorganiser"><i class="fa-solid fa-grip-vertical"></i></span>
        <input type="text" class="jf-section-card__title"
               value="${this._esc(section.title || "")}"
               placeholder="Titre de la section"
               data-action="blur->jestionform-form-builder#saveSectionTitle"
               data-section-id="${section.id}" />
        <span class="jf-section-card__count">0 champ</span>
        <button type="button" class="jf-section-card__delete" title="Supprimer cette section"
                data-action="click->jestionform-form-builder#deleteSection"
                data-section-id="${section.id}">
          <i class="fa-solid fa-trash-can"></i>
        </button>
      </header>
      <div class="jf-section-card__fields"
           data-jestionform-form-builder-target="fieldsList"
           data-section-id="${section.id}">
        <p class="jf-section-card__empty">Aucun champ dans cette section.</p>
      </div>
      <footer class="jf-section-card__foot">
        <button type="button" class="btn-ds btn-ds--ghost btn-ds--sm"
                data-action="click->jestionform-form-builder#openFieldPicker"
                data-section-id="${section.id}">
          <i class="fa-solid fa-plus"></i> Ajouter un champ
        </button>
      </footer>
    `;
    this.sectionsListTarget.appendChild(card);
    // Re-init Sortables to register the new fields list.
    this._initSortables();
  }

  _appendFieldCard(sectionId, field) {
    const sectionCard = this.sectionCardTargets.find((c) => c.dataset.sectionId === sectionId);
    if (!sectionCard) return;
    const list = sectionCard.querySelector('[data-jestionform-form-builder-target="fieldsList"]');
    const empty = list?.querySelector(".jf-section-card__empty");
    empty?.remove();

    const typeLabel = this._typeLabel(field.field_type);
    const icon = this._typeIcon(field.field_type);
    const choicesArr = Array.isArray(field?.options?.choices) ? field.options.choices : [];
    const isOptionType = this.constructor.OPTION_TYPES.includes(field.field_type);
    const optionsLine = isOptionType
      ? ` · ${choicesArr.length} option${choicesArr.length > 1 ? "s" : ""}${choicesArr.length === 0 ? " ⚠ aucune option configurée" : ""}`
      : "";

    const payload = {
      id:          field.id,
      label:       field.label,
      field_type:  field.field_type,
      type_label:  typeLabel,
      help_text:   field.help_text || "",
      placeholder: field.placeholder || "",
      required:    !!field.required,
      choices:     choicesArr.map(String)
    };

    const card = document.createElement("div");
    card.className = "jf-field-card";
    card.dataset.fieldId = field.id;
    card.dataset.sectionId = sectionId;
    card.dataset.fieldPayload = JSON.stringify(payload);
    card.setAttribute("data-jestionform-form-builder-target", "fieldCard");
    card.innerHTML = `
      <span class="jf-field-card__handle" title="Glisser pour réorganiser"><i class="fa-solid fa-grip-vertical"></i></span>
      <span class="jf-field-card__icon" title="${this._esc(typeLabel)}"><i class="fa-solid ${icon}"></i></span>
      <div class="jf-field-card__body">
        <strong class="jf-field-card__label">${this._esc(field.label || "(sans libellé)")}${field.required ? ' <span class="jf-field-card__required" title="Obligatoire">*</span>' : ""}</strong>
        <span class="jf-field-card__type">${this._esc(typeLabel)}${this._esc(optionsLine)}</span>
      </div>
      <div class="jf-field-card__actions">
        <button type="button" class="jf-icon-btn" title="Modifier le champ"
                data-action="click->jestionform-form-builder#editField"
                data-field-id="${field.id}">
          <i class="fa-solid fa-pen"></i>
        </button>
        <button type="button" class="jf-icon-btn jf-icon-btn--toggle ${field.required ? 'is-active' : ''}"
                title="${field.required ? 'Retirer obligatoire' : 'Rendre obligatoire'}"
                data-action="click->jestionform-form-builder#toggleRequired"
                data-field-id="${field.id}">
          <i class="fa-solid fa-asterisk"></i>
        </button>
        <button type="button" class="jf-icon-btn jf-icon-btn--danger" title="Supprimer ce champ"
                data-action="click->jestionform-form-builder#deleteField"
                data-field-id="${field.id}">
          <i class="fa-solid fa-trash-can"></i>
        </button>
      </div>
    `;
    list.appendChild(card);
    this._refreshSectionCount(sectionId);
  }

  _refreshSectionCount(sectionId) {
    if (!sectionId) return;
    const sectionCard = this.sectionCardTargets.find((c) => c.dataset.sectionId === sectionId);
    if (!sectionCard) return;
    const count = sectionCard.querySelectorAll(".jf-field-card").length;
    const badge = sectionCard.querySelector(".jf-section-card__count");
    if (badge) badge.textContent = `${count} champ${count > 1 ? "s" : ""}`;
  }

  _refreshEmptyState() {
    const stillSections = this.sectionCardTargets.length > 0;
    if (!stillSections && !this.hasEmptyTarget) {
      // Could re-create the empty state, but a page reload covers it cleanly.
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
    };
  }

  _typeLabel(type) {
    return ({
      text: "Texte court", textarea: "Texte long", email: "Email", phone: "Téléphone",
      number: "Nombre", date: "Date", select: "Liste déroulante", multiselect: "Multi-sélection",
      radio: "Choix unique", checkbox: "Cases à cocher", file: "Fichier", rating: "Note (étoiles)",
      signature: "Signature", hidden: "Champ caché"
    })[type] || (type || "");
  }

  _typeIcon(type) {
    return ({
      text: "fa-font", textarea: "fa-align-left", email: "fa-envelope", phone: "fa-phone",
      number: "fa-hashtag", date: "fa-calendar", select: "fa-caret-down", multiselect: "fa-list-check",
      radio: "fa-dot-circle", checkbox: "fa-check-square", file: "fa-paperclip", rating: "fa-star",
      signature: "fa-signature", hidden: "fa-eye-slash"
    })[type] || "fa-square";
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }
}
